/**
 * The Informed Consent Form download, shared by the Study Design page's
 * Consent Form tab and the Study Documents card. The document is built in the
 * browser from the consent artifact's content (the same plain Word build Study
 * Design has always used); the caller supplies the server pre-check, which
 * must be `downloadBlockedReason(projectId, 'consent_form')`.
 */
import { DOWNLOAD_DOC_NAMES, type DownloadRequest } from './studyDocumentDownloads';

export interface ConsentDownloadInput {
  /** The consent artifact's content (markdown or structured JSON). */
  content: unknown;
  projectName?: string;
  /** A saved earlier version; omitted for the current one. */
  version?: number;
  precheck: () => Promise<string | null>;
}

/** The Word title, which also names the file. Unchanged from Study Design. */
export function consentTitle(projectName: string | undefined, version?: number): string {
  const base = projectName ? `${projectName} - Informed Consent Form` : 'Informed Consent Form';
  return version !== undefined ? `${base} v${version}` : base;
}

export function consentDownloadRequest(input: ConsentDownloadInput): DownloadRequest {
  const title = consentTitle(input.projectName, input.version);
  return {
    id: input.version !== undefined ? `consentForm:word:v${input.version}` : 'consentForm:word',
    docName: DOWNLOAD_DOC_NAMES.consentForm,
    precheck: input.precheck,
    build: async () => {
      const { buildDocx } = await import('../../utils/docxGenerator');
      return buildDocx(input.content, title);
    },
  };
}

/**
 * Which consent artifact a page downloads: the LAST `consent_form` in
 * Firestore's order, which is what Study Design has always shown. The Study
 * Documents card uses the same rule, so both always download the same consent.
 */
export function selectConsentArtifact<T extends { type?: unknown }>(artifacts: readonly T[]): T | null {
  let found: T | null = null;
  for (const a of artifacts) if (a?.type === 'consent_form') found = a;
  return found;
}
